import { useContext, useState } from 'react';
import useI18n from '@/hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { Campaign } from '@/interfaces/campaign';
import {
  CampaignDashboardKpis,
  CampaignTopPosts,
} from '@/interfaces/campaignKpis';
import { ILink } from '@/interfaces/Link';
import { formatCampaignDate } from '@/utils/dateUtils';
import { getUsedPlatforms } from '@/utils/socialplatformUtils';
import { useNavigate } from 'react-router-dom';
import { generateSmartLink } from '@/utils/smartLinkUtils';
import Model from '@/components/common/models/Model';
import LinkShareCard from '../LinkShareCard/LinkShare';
import { toCampaignShareLink } from '@/utils/campaignShareLinkUtils';
import {
  useCampaignKpisQuery,
  useCampaignTopPostsQuery,
  useCampaignCreatorsReachQuery,
} from '@/hooks/queries/useAnalyticsQueries';
import { useGetBrandCampaignsPaginatedQuery } from '@/hooks/queries/useCampaignQueries';
import { ChevronRightIcon } from '@/svg';
import CampaignSummaryRow from './campaign/CampaignSummaryRow';
import NoSubmissionsBanner from './campaign/NoSubmissionsBanner';
import CampaignKpiCards from './campaign/CampaignKpiCards';
import CampaignStatsGrid from './campaign/CampaignStatsGrid';
import TopPostsPanel from './campaign/TopPostsPanel';
import CreatorsReachPanel from './campaign/CreatorsReachPanel';
import {
  CampaignSectionSkeleton,
  CreatorsReachSkeleton,
  KpiCardsSkeleton,
  StatsGridSkeleton,
  TopPostsSkeleton,
} from './campaign/CampaignSkeletons';
import {
  DUMMY_TOP_POSTS,
  DUMMY_TOP_POSTS_TOTAL,
  DUMMY_CREATORS_REACH,
} from './campaign/dummyCampaignInsights';

// Placeholder only: the API sends the benchmarks. €0.50 is its fixed CPE.
const BENCHMARKS = { paidSocialCpe: 0.5, metaAdsEngagementRate: 3 };

const EMPTY_KPIS: CampaignDashboardKpis = {
  totalSpent: 0,
  totalImpressions: 0,
  totalPosts: 0,
  totalLikes: 0,
  totalShares: 0,
  totalSales: 0,
  engagementRate: 0,
  roas: 0,
  costPerEngagement: null,
  cheaperPercent: null,
  benchmarks: BENCHMARKS,
};

// Derived figures kept consistent with the totals: 4600 / 1840 = 2.5x return,
// 1840 / (18300 + 2400) = EUR 0.09 per engagement, 1 - 0.09 / 0.50 = 82% cheaper.
const DUMMY_CAMPAIGN_KPIS: CampaignDashboardKpis = {
  totalSpent: 1840,
  totalImpressions: 327_000,
  totalPosts: 92,
  totalLikes: 18_300,
  totalShares: 2_400,
  totalSales: 4600,
  engagementRate: 8,
  roas: 2.5,
  costPerEngagement: 0.09,
  cheaperPercent: 82,
  benchmarks: BENCHMARKS,
};

const EMPTY_TOP_POSTS: CampaignTopPosts = { totalCount: 0, posts: [] };

const CampaignSection = ({
  campaignStatus,
}: {
  campaignStatus: 'live' | 'paused';
}) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  const navigate = useNavigate();

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }

  const { isDummyUser } = userContext;
  const [copyLink, setCopyLink] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { data: campaignData, isLoading: isCampaignLoading } =
    useGetBrandCampaignsPaginatedQuery(campaignStatus, 1, 1);

  const activeCampaigns: (Campaign & Record<string, any>)[] =
    (campaignData as any)?.campaigns ?? [];

  const campaign = activeCampaigns[0];

  // Scoped to the campaign on show rather than to every campaign the brand has
  // run, so these figures match the detail page behind "Go inside campaign".
  const {
    data: kpiData,
    isLoading: isKpisLoading,
    isError: isKpisError,
  } = useCampaignKpisQuery(campaign?._id ?? '', !isDummyUser);

  const { data: topPostsData, isLoading: isTopPostsLoading } =
    useCampaignTopPostsQuery(campaign?._id ?? '', !isDummyUser);

  const { data: reachData, isLoading: isReachLoading } =
    useCampaignCreatorsReachQuery(campaign?._id ?? '', !isDummyUser);
  const reach = isDummyUser ? DUMMY_CREATORS_REACH : reachData;

  const kpis = isDummyUser ? DUMMY_CAMPAIGN_KPIS : (kpiData ?? EMPTY_KPIS);

  const topPosts = isDummyUser
    ? { posts: DUMMY_TOP_POSTS, totalCount: DUMMY_TOP_POSTS_TOTAL }
    : (topPostsData ?? EMPTY_TOP_POSTS);

  // Each panel waits on its own request only, so the fast ones render while
  // the slow ones still show a skeleton. Dummy users never fetch.
  const kpisPending = !isDummyUser && isKpisLoading;
  const topPostsPending = !isDummyUser && isTopPostsLoading;
  const reachPending = !isDummyUser && isReachLoading;

  // Nothing posted yet, so every metric reads as an em dash or zero and the
  // banner explains why rather than leaving the panel looking broken. A failed
  // fetch also reads as zero posts, but must not claim there are none.
  const hasSubmissions = kpis.totalPosts > 0;
  const showNoSubmissions = !kpisPending && !hasSubmissions && !isKpisError;
  // Top posts gate on their own count, not the KPIs, so they need not wait.
  const hasTopPosts = topPosts.totalCount > 0;

  const startDate = formatCampaignDate(campaign?.startDate, false);
  const endDate = formatCampaignDate(campaign?.endDate ?? '', false);
  const dateRange = `${startDate} - ${
    campaign?.runCampaignTillMoneyEnd ? t('common.untilBudgetEnds') : endDate
  }`;
  const platform = getUsedPlatforms(campaign?.containType);

  const budget = isDummyUser ? 10000 : (campaign?.campaignBudget ?? 0);
  const remainingBudget = isDummyUser
    ? 10000 - 1840
    : (campaign?.remainingCampaignBudget ?? 0);
  const remainingPercent = budget > 0 ? (remainingBudget / budget) * 100 : 0;

  const handleShareModalClose = () => setIsShareModalOpen(false);
  const handleShareModalOpen = (id: string) => {
    const found = activeCampaigns.find((item) => item._id === id);
    if (found && found.smartLinkId) {
      const smartLink = generateSmartLink(found.smartLinkId as ILink);
      setCopyLink(toCampaignShareLink(smartLink, found.status === 'live'));
    } else {
      setCopyLink('');
    }
    setIsShareModalOpen(true);
  };

  if (isCampaignLoading) {
    return <CampaignSectionSkeleton />;
  }

  if (activeCampaigns.length === 0) {
    return null;
  }

  // Real content fades in over its skeleton rather than snapping into place.
  const fadeIn = 'motion-safe:animate-fade-in';

  return (
    <div
      data-testid="campaign-section"
      aria-busy={kpisPending || topPostsPending || reachPending}
      className={`flex flex-col items-center px-6 ${fadeIn}`}
    >
      <div className="flex w-full flex-col gap-4 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col justify-center">
          <p className="font-header text-base font-semibold capitalize leading-6 tracking-[0.096px] text-[#172B4D]">
            {t('common.campaign')}
          </p>
          <p className="font-header text-sm capitalize leading-6 tracking-[0.084px] text-[#9B919D]">
            {t('common.seeCreatorsPostsAndSalesBehindTheseNumbers')}
          </p>
        </div>
        <button
          type="button"
          data-testid="go-inside-campaign-button"
          onClick={() => campaign?._id && navigate(`/campaign/${campaign._id}`)}
          className="flex items-center justify-center gap-2 rounded-full bg-[#231254] px-6 py-3 font-header text-sm font-bold capitalize tracking-[0.21px] text-white transition-colors hover:bg-[#1B0E42] max-lg:w-full"
        >
          {t('common.goInsideCampaign')}
          <ChevronRightIcon stroke="#FFFFFF" />
        </button>
      </div>

      <div
        data-testid="campaign-details"
        className="flex w-full flex-col gap-4 rounded-3xl border border-[#E9E9E9] bg-[#F9F9F9] p-5 lg:p-6"
      >
        <CampaignSummaryRow
          brandName={
            campaign?.brandName
              ? campaign.brandName.charAt(0).toUpperCase() +
                campaign.brandName.slice(1)
              : ''
          }
          dateRange={dateRange}
          isLive={campaignStatus === 'live'}
          budget={budget}
          remainingBudget={remainingBudget}
          remainingPercent={remainingPercent}
          platform={platform}
          onShare={() => handleShareModalOpen(campaign?._id ?? '')}
        />

        {showNoSubmissions && <NoSubmissionsBanner />}

        {kpisPending ? (
          <>
            <KpiCardsSkeleton />
            <StatsGridSkeleton />
          </>
        ) : (
          <div className={`flex flex-col gap-4 ${fadeIn}`}>
            <CampaignKpiCards
              hasSubmissions={hasSubmissions}
              totalSales={kpis.totalSales}
              totalSpent={kpis.totalSpent}
              roas={kpis.roas}
              totalEngagements={kpis.totalLikes + kpis.totalShares}
              costPerEngagement={kpis.costPerEngagement}
              cheaperPercent={kpis.cheaperPercent}
              engagementRate={kpis.engagementRate}
              benchmarks={kpis.benchmarks}
            />

            <CampaignStatsGrid
              totalViews={kpis.totalImpressions}
              totalLikes={kpis.totalLikes}
              totalShares={kpis.totalShares}
              totalPosts={kpis.totalPosts}
            />
          </div>
        )}
      </div>

      {(topPostsPending ||
        hasTopPosts ||
        reachPending ||
        !!reach?.audienceCreatorCount) && (
        <div
          data-testid="campaign-insights"
          className="flex w-full flex-col gap-6 pt-6"
        >
          {topPostsPending ? (
            <TopPostsSkeleton />
          ) : (
            hasTopPosts && (
              <div className={fadeIn}>
                <TopPostsPanel
                  posts={topPosts.posts}
                  totalCount={topPosts.totalCount}
                  onViewAll={() =>
                    campaign?._id && navigate(`/campaign/${campaign._id}`)
                  }
                />
              </div>
            )
          )}
          {/* Hidden until Meta has demographics for at least one creator. */}
          {reachPending ? (
            <CreatorsReachSkeleton />
          ) : (
            !!reach?.audienceCreatorCount && (
              <div className={fadeIn}>
                <CreatorsReachPanel reach={reach} />
              </div>
            )
          )}
        </div>
      )}

      {isShareModalOpen && (
        <Model isOpen={isShareModalOpen} onClose={handleShareModalClose}>
          <LinkShareCard
            handleShareModalClose={handleShareModalClose}
            link={copyLink}
          />
        </Model>
      )}
    </div>
  );
};

export default CampaignSection;
